import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { validate as isUuid } from 'uuid'
import {
  clearStoredAssignedFarmsFilters,
  readStoredAssignedFarmsFilters,
  writeStoredAssignedFarmsFilters,
} from '@/services/appPreferences/assignedFarmsFiltersStorage'
import type {
  AssignedFarmsContractStatusFilter,
  AssignedFarmsQuery,
  AssignedFarmsSort,
  AssignedFarmsSortField,
} from '@/types/apiModels'
import { useDebouncedValue } from './useDebouncedValue'

type Sort = AssignedFarmsSort
type Status = AssignedFarmsContractStatusFilter

const PAGE_SIZE = 10
const SEARCH_DEBOUNCE_MS = 300
const DEFAULT_SORT: Sort = 'name:asc'
const SORT_FIELDS: AssignedFarmsSortField[] = ['name', 'program', 'hub']
const VALID_SORTS: Sort[] = SORT_FIELDS.flatMap(field => [`${field}:asc`, `${field}:desc`] as const)
const STATUS_VALUES: Status[] = ['signed', 'terminating', 'terminated']
const FILTER_PARAM_KEYS = ['q', 'program', 'hub', 'status', 'sort'] as const

const parseWholeNumber = (value: string | null): number | undefined => {
  if (!value || !/^\d+$/.test(value)) return undefined
  return Number(value)
}

// PROGRAM_IDS.SARA is 0 (see useExclusionAreaTypes), so programId must accept zero;
// page is the only 1-based value here and rejects it.
const parseProgramId = (value: string | null): number | undefined => parseWholeNumber(value)

const parsePage = (value: string | null): number => {
  const parsed = parseWholeNumber(value)
  return parsed !== undefined && parsed > 0 ? parsed : 1
}

const parseHubId = (value: string | null): string | undefined =>
  value && isUuid(value) ? value : undefined

const parseSort = (value: string | null): Sort =>
  value && (VALID_SORTS as string[]).includes(value) ? (value as Sort) : DEFAULT_SORT

const parseStatus = (value: string | null): Status | undefined =>
  value && (STATUS_VALUES as string[]).includes(value) ? (value as Status) : undefined

export const useAssignedFarmsFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  // setSearchParams changes identity every render; a stable ref keeps the debounce-commit effect
  // from re-firing and clobbering a just-restored value.
  const setSearchParamsRef = useRef(setSearchParams)
  setSearchParamsRef.current = setSearchParams
  const restoredFromStorage = useRef(false)

  // Trimmed here (not just at commit time) so committedSearchRef/searchInput seed from the same
  // value the debounce-commit effect compares against, and an untrimmed deep link never triggers
  // a spurious mount-time commit that would also strip the page param.
  const search = (searchParams.get('q') ?? '').trim()
  const programId = parseProgramId(searchParams.get('program'))
  const hubId = parseHubId(searchParams.get('hub'))
  const status = parseStatus(searchParams.get('status'))
  const page = parsePage(searchParams.get('page'))
  const sort = parseSort(searchParams.get('sort'))

  const [searchInput, setSearchInput] = useState(search)
  const debouncedSearchInput = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS)
  const committedSearchRef = useRef(search)

  const commit = useCallback(
    (
      mutate: (params: URLSearchParams) => void,
      { resetPage = true }: { resetPage?: boolean } = {},
    ) => {
      setSearchParamsRef.current(
        current => {
          const next = new URLSearchParams(current)
          mutate(next)
          if (resetPage) next.delete('page')

          const nextSort = next.get('sort')
          const nextStatus = next.get('status')
          writeStoredAssignedFarmsFilters({
            q: next.get('q') || undefined,
            program: next.get('program') || undefined,
            hub: next.get('hub') || undefined,
            status:
              nextStatus && (STATUS_VALUES as string[]).includes(nextStatus)
                ? (nextStatus as Status)
                : undefined,
            sort:
              nextSort && nextSort !== DEFAULT_SORT && (VALID_SORTS as string[]).includes(nextSort)
                ? (nextSort as Exclude<Sort, typeof DEFAULT_SORT>)
                : undefined,
          })

          return next
        },
        { replace: true },
      )
    },
    [],
  )

  // A fresh visit (no filter param at all) inherits the last session's filters; any filter param
  // already present means the user followed a link or pressed Back, and that URL must win outright.
  useEffect(() => {
    if (restoredFromStorage.current) return
    restoredFromStorage.current = true
    if (searchParams.has('page') || FILTER_PARAM_KEYS.some(key => searchParams.has(key))) return

    const stored = readStoredAssignedFarmsFilters()
    if (!stored) return

    setSearchParamsRef.current(
      current => {
        const next = new URLSearchParams(current)
        if (stored.q) next.set('q', stored.q)
        if (stored.program) next.set('program', stored.program)
        if (stored.hub) next.set('hub', stored.hub)
        if (stored.status) next.set('status', stored.status)
        if (stored.sort) next.set('sort', stored.sort)
        return next
      },
      { replace: true },
    )
  }, [searchParams])

  // Keeps the input in sync whenever the committed URL search changes for a reason other than the
  // debounce commit below (Back/Forward, the storage restore, clearFilters).
  useEffect(() => {
    if (search === committedSearchRef.current) return
    committedSearchRef.current = search
    setSearchInput(search)
  }, [search])

  useEffect(() => {
    const trimmedSearch = debouncedSearchInput.trim()
    if (trimmedSearch === committedSearchRef.current) return
    committedSearchRef.current = trimmedSearch
    commit(params => {
      if (trimmedSearch) params.set('q', trimmedSearch)
      else params.delete('q')
    })
  }, [debouncedSearchInput, commit])

  const setProgramId = useCallback(
    (id: number | undefined) =>
      commit(params => {
        if (id !== undefined) params.set('program', String(id))
        else params.delete('program')
      }),
    [commit],
  )

  const setHubId = useCallback(
    (id: string | undefined) =>
      commit(params => {
        if (id !== undefined) params.set('hub', id)
        else params.delete('hub')
      }),
    [commit],
  )

  const setStatus = useCallback(
    (nextStatus: Status | undefined) =>
      commit(params => {
        if (nextStatus !== undefined) params.set('status', nextStatus)
        else params.delete('status')
      }),
    [commit],
  )

  const setSort = useCallback(
    (nextSort: Sort) =>
      commit(params => {
        if (nextSort !== DEFAULT_SORT) params.set('sort', nextSort)
        else params.delete('sort')
      }),
    [commit],
  )

  const toggleSort = useCallback(
    (field: AssignedFarmsSortField) => {
      const [activeField, activeDirection] = sort.split(':') as [
        AssignedFarmsSortField,
        'asc' | 'desc',
      ]
      const nextSort: Sort =
        activeField === field
          ? `${field}:${activeDirection === 'asc' ? 'desc' : 'asc'}`
          : `${field}:asc`
      setSort(nextSort)
    },
    [sort, setSort],
  )

  const setPage = useCallback(
    (nextPage: number) =>
      commit(
        params => {
          if (nextPage > 1) params.set('page', String(nextPage))
          else params.delete('page')
        },
        { resetPage: false },
      ),
    [commit],
  )

  const clearFilters = useCallback(() => {
    // Without this, a search typed inside the debounce window survives clearFilters and commits
    // right back into the URL and storage once the pending timer fires (R3-003).
    committedSearchRef.current = ''
    setSearchInput('')
    setSearchParamsRef.current(
      current => {
        const next = new URLSearchParams(current)
        FILTER_PARAM_KEYS.forEach(key => next.delete(key))
        next.delete('page')
        return next
      },
      { replace: true },
    )
    clearStoredAssignedFarmsFilters()
  }, [])

  const hasActiveFilters = Boolean(
    search ||
      programId !== undefined ||
      hubId !== undefined ||
      status !== undefined ||
      sort !== DEFAULT_SORT,
  )

  const apiQuery = useMemo<AssignedFarmsQuery>(
    () => ({
      search: search || undefined,
      programId,
      hubId,
      contractStatus: status,
      page: page - 1,
      size: PAGE_SIZE,
      sort,
    }),
    [search, programId, hubId, status, page, sort],
  )

  return {
    searchInput,
    search,
    programId,
    hubId,
    status,
    page,
    sort,
    setSearchInput,
    setProgramId,
    setHubId,
    setStatus,
    setPage,
    setSort,
    toggleSort,
    clearFilters,
    hasActiveFilters,
    apiQuery,
  }
}
